/**
 * Verify Insurance Actor
 *
 * Handles insurance verification only.
 * Separated from pricing to allow independent invocation and error handling.
 */

import { getInsuranceStatuses } from "@frontend/lib/actions/actions";
import { getInsuranceByConsumerIdAndExcludeStatus } from "@frontend/lib/actions/v2actions";
import {
  InsuranceAccountStatus,
  InsurancesStatusEnum,
} from "@frontend/lib/graphql/generated-gql";
import { RequestSource } from "@frontend/lib/schemas/clientSchema";
import { fromPromise } from "xstate";
import { z } from "zod";
import { getStateIdFromUrl } from "../../devtool/playwright-generator";
import {
  buildInsuranceResult,
  mapInsuranceStatusToState,
} from "../helper/insurance-helpers";
import type { ActorWithMetadata } from "./send-sms.actor";

/**
 * Input Schema
 */
const VerifyInsuranceInputSchema = z.object({
  // Consumer data
  consumerId: z.string().optional().nullable(),
  firstName: z.string(),
  lastName: z.string(),
  dateOfBirth: z.string(), // MM-dd-yyyy format

  // Insurance data
  insurances: z.array(
    z.object({
      memberId: z.string(),
      payer: z.string(),
      state: z.string(),
      ehrInsuranceId: z.string().optional().nullable(),
      payerId: z.string().optional().nullable(),
      id: z.string().optional(),
      insuredFirstName: z.string().optional().nullable(),
      insuredLastName: z.string().optional().nullable(),
    }),
  ),

  // Organization context
  orgId: z.number(),

  // User email for requests
  email: z.string().email().or(z.literal("")),
});

type VerifyInsuranceInput = z.infer<typeof VerifyInsuranceInputSchema>;

/**
 * Success Schema
 */
const VerifyInsuranceSuccessSchema = z.object({
  insurances: z.object({
    primaryInsurance: z
      .object({
        payerId: z.string().optional().nullable(),
        status: z.nativeEnum(InsurancesStatusEnum),
        ehrInsuranceId: z.string().optional().nullable(),
        error: z.string().optional().nullable(),
        active: z.boolean().optional(),
        verified: z.boolean().optional(),
      })
      .optional(),
    secondaryInsurance: z
      .object({
        payerId: z.string().optional().nullable(),
        status: z.nativeEnum(InsurancesStatusEnum),
        ehrInsuranceId: z.string().optional().nullable(),
        error: z.string().optional().nullable(),
        active: z.boolean().optional(),
        verified: z.boolean().optional(),
      })
      .optional(),
  }),
  insuranceStates: z
    .object({
      primary: z.object({
        inNetwork: z.boolean(),
        active: z.boolean(),
        withBenefits: z.boolean(),
        verified: z.boolean(),
      }),
      secondary: z
        .object({
          inNetwork: z.boolean(),
          active: z.boolean(),
          withBenefits: z.boolean(),
          verified: z.boolean(),
        })
        .optional(),
    })
    .optional(),
  insuranceStatuses: z.array(z.any()).optional(), // Full insurance status array for debugging
});

export type VerifyInsuranceSuccess = z.infer<
  typeof VerifyInsuranceSuccessSchema
>;

/**
 * Error Schema
 */
const VerifyInsuranceErrorSchema = z.object({
  code: z.enum([
    "VALIDATION_ERROR",
    "INSURANCE_VERIFICATION_FAILED",
    "INSURANCE_INACTIVE",
    "OUT_OF_NETWORK",
    "NETWORK_ERROR",
  ]),
  message: z.string(),
  retryable: z.boolean(),
  insuranceError: z.string().optional().nullable(), // Specific insurance error message
  insuranceStates: z
    .object({
      primary: z.object({
        inNetwork: z.boolean(),
        active: z.boolean(),
        withBenefits: z.boolean(),
        verified: z.boolean(),
      }),
      secondary: z
        .object({
          inNetwork: z.boolean(),
          active: z.boolean(),
          withBenefits: z.boolean(),
          verified: z.boolean(),
        })
        .optional(),
    })
    .optional(),
  insuranceStatuses: z.array(z.any()).optional(), // Full insurance status array for debugging
});

type VerifyInsuranceError = z.infer<typeof VerifyInsuranceErrorSchema>;

/**
 * Creates a structured error with code and metadata
 */
function createStructuredError<T extends Record<string, unknown>>(
  data: T & { message: string },
): Error & T {
  const error = new Error(data.message);
  Object.assign(error, data);
  return error as Error & T;
}

/**
 * Dependencies
 */
export interface VerifyInsuranceDependencies {
  analytics?: {
    track: (event: string, properties?: Record<string, unknown>) => void;
  };
}

/**
 * Verify Insurance Actor Implementation
 */
const verifyInsuranceActorImpl = fromPromise(
  async ({
    input,
  }: {
    input: VerifyInsuranceInput & { deps?: VerifyInsuranceDependencies };
  }): Promise<VerifyInsuranceSuccess> => {
    const {
      consumerId,
      firstName,
      lastName,
      dateOfBirth,
      insurances,
      orgId,
      email,
      deps,
    } = input;

    const fixtureId = getStateIdFromUrl();

    // Validate input
    const validated = VerifyInsuranceInputSchema.safeParse({
      consumerId,
      firstName,
      lastName,
      dateOfBirth,
      insurances,
      orgId,
      email,
    });

    if (!validated.success) {
      throw createStructuredError<VerifyInsuranceError>({
        code: "VALIDATION_ERROR",
        message: `Validation failed: ${validated.error.message}`,
        retryable: false,
      });
    }

    try {
      const consumer = {
        id: consumerId,
        fname: firstName,
        lname: lastName,
        dob: dateOfBirth,
      };

      // Fetch existing insurance data IDs if consumer exists
      // Match on priority + payer + memberId + state to avoid leaking an old
      // insurance's ID onto newly-entered insurance (PLA-1183).
      const { primaryInsuranceDataId, secondaryInsuranceDataId } = consumerId
        ? await getInsuranceByConsumerIdAndExcludeStatus(consumerId, [
            InsuranceAccountStatus.Deleted,
            InsuranceAccountStatus.Replaced,
          ]).then((existingInsurances) => ({
            primaryInsuranceDataId: existingInsurances.find(
              (ins) =>
                ins.priority === 1 &&
                ins.payer === insurances[0]?.payer &&
                ins.memberId === insurances[0]?.memberId &&
                ins.state === insurances[0]?.state,
            )?.id,
            secondaryInsuranceDataId: existingInsurances.find(
              (ins) =>
                ins.priority === 2 &&
                ins.payer === insurances[1]?.payer &&
                ins.memberId === insurances[1]?.memberId &&
                ins.state === insurances[1]?.state,
            )?.id,
          }))
        : {
            primaryInsuranceDataId: undefined,
            secondaryInsuranceDataId: undefined,
          };

      // Ensure insuredFirstName/insuredLastName are derived from subscriberName
      // if not already set (PLA-1213: OCR name correction only writes subscriberName)
      const enrichedInsurances = insurances.map((ins) => {
        if (ins.insuredFirstName && ins.insuredLastName) return ins;
        const subName = (ins as Record<string, unknown>).subscriberName;
        if (typeof subName === "string" && subName.trim()) {
          const parts = subName.trim().split(/\s+/);
          return {
            ...ins,
            insuredFirstName: ins.insuredFirstName ?? parts[0] ?? firstName,
            insuredLastName:
              ins.insuredLastName ?? (parts.slice(1).join(" ") || lastName),
          };
        }
        return {
          ...ins,
          insuredFirstName: ins.insuredFirstName ?? firstName,
          insuredLastName: ins.insuredLastName ?? lastName,
        };
      });

      const insuranceStatuses = await getInsuranceStatuses(
        consumer,
        enrichedInsurances,
        orgId,
        RequestSource.KIOSK,
        email ?? "",
        undefined, // global org context
        undefined, // no provider indicator for kiosk
      );

      // Upload insurance response to S3 for test replay (fire-and-forget)
      if (fixtureId && insuranceStatuses.length > 0) {
        fetch("/api/fixtures/upload", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            fixtureId,
            insurance: insuranceStatuses,
          }),
        }).catch((err) => {
          console.warn("[verify-insurance] Failed to upload fixture:", err);
        });
      }

      // Cache array accesses for performance (js-cache-property-access)
      const primaryStatus = insuranceStatuses[0];
      const secondaryStatus = insuranceStatuses[1];

      const allActive = insuranceStatuses.every(
        (ins) =>
          ins.status === InsurancesStatusEnum.Active ||
          ins.status === InsurancesStatusEnum.ActiveBenefitsNotReturned,
      );

      // Use helper functions to reduce duplication
      const primaryState = mapInsuranceStatusToState(primaryStatus);
      const secondaryState = secondaryStatus
        ? mapInsuranceStatusToState(secondaryStatus)
        : undefined;

      const primaryInsurance = buildInsuranceResult(
        primaryStatus,
        insurances[0],
        primaryInsuranceDataId,
      );

      const secondaryInsurance = secondaryStatus
        ? buildInsuranceResult(
            secondaryStatus,
            insurances[1],
            secondaryInsuranceDataId,
          )
        : undefined;

      const result: VerifyInsuranceSuccess & { pendingPricesMode?: boolean } = {
        insurances: {
          primaryInsurance,
          secondaryInsurance,
        },
        insuranceStates: {
          primary: primaryState,
          ...(secondaryStatus && { secondary: secondaryState }),
        },
        insuranceStatuses,
        // Set pendingPricesMode if primary is unverified or active with no benefits
        // Note: backend may return status "active" with hasBenefits: false
        ...((primaryStatus.status === InsurancesStatusEnum.Unverified ||
          primaryStatus.status ===
            InsurancesStatusEnum.ActiveBenefitsNotReturned ||
          (primaryStatus.status === InsurancesStatusEnum.Active &&
            !primaryStatus.hasBenefits)) && {
          pendingPricesMode: true,
        }),
      };

      // Early exit for inactive insurance (js-early-exit)
      if (!allActive) {
        const firstError = insuranceStatuses.find((ins) => ins.error)?.error;
        throw createStructuredError<
          VerifyInsuranceError & { insurances?: typeof result.insurances }
        >({
          code: "INSURANCE_INACTIVE",
          message: firstError || "Insurance verification failed",
          retryable: false,
          insuranceError: firstError,
          insuranceStates: {
            primary: primaryState,
            secondary: secondaryState,
          },
          insuranceStatuses,
          insurances: result.insurances,
        });
      }

      /**
       * If the primary insurance is out-of-network, we throw a specific error to trigger
       */
      if (primaryState.inNetwork === false) {
        throw createStructuredError<
          VerifyInsuranceError & { insurances?: typeof result.insurances }
        >({
          code: "OUT_OF_NETWORK",
          message: "Out-of-Network insurance detected",
          retryable: false,
          insuranceError: "Out-of-Network insurance detected",
          insuranceStates: {
            primary: primaryState,
            secondary: secondaryState,
          },
          insuranceStatuses,
          insurances: result.insurances,
        });
      }

      /**
       * If the secondary insurance is out-of-network (primary is in-network), throw so
       * the arc can offer the patient the option to proceed with primary only.
       */
      if (secondaryState && secondaryState.inNetwork === false) {
        throw createStructuredError<
          VerifyInsuranceError & { insurances?: typeof result.insurances }
        >({
          code: "OUT_OF_NETWORK",
          message: "Secondary insurance is out-of-network",
          retryable: false,
          insuranceError: "Secondary insurance is out-of-network",
          insuranceStates: {
            primary: primaryState,
            secondary: secondaryState,
          },
          insuranceStatuses,
          insurances: result.insurances,
        });
      }

      return result;
    } catch (error) {
      // Re-throw structured errors as-is
      if (error instanceof Error && "code" in error) {
        throw error;
      }

      throw createStructuredError<VerifyInsuranceError>({
        code: "INSURANCE_VERIFICATION_FAILED",
        message:
          error instanceof Error
            ? error.message
            : "Insurance verification failed",
        retryable: true,
      });
    }
  },
);

/**
 * Verify Insurance Actor with Metadata
 */
export const verifyInsuranceActor: ActorWithMetadata = {
  impl: verifyInsuranceActorImpl,
  metadata: {
    displayName: "verifyInsurance",
    description: "Verify insurance eligibility and status",
    category: "data",
    retryable: true,
    timeout: 30000, // 30 seconds
    errorCodes: [
      "VALIDATION_ERROR",
      "INSURANCE_VERIFICATION_FAILED",
      "INSURANCE_INACTIVE",
      "NETWORK_ERROR",
    ],
    inputSchema: VerifyInsuranceInputSchema,
    successSchema: VerifyInsuranceSuccessSchema,
    errorSchema: VerifyInsuranceErrorSchema,
    onSuccess: (result: unknown) => {
      const success = result as VerifyInsuranceSuccess & {
        pendingPricesMode?: boolean;
      };
      const output = {
        insuranceVerification: success,
        insuranceError: undefined,
        pendingPricesMode: success.pendingPricesMode,
      };
      return output;
    },
    onError: (error: unknown) => {
      const err = error as VerifyInsuranceError & {
        insurances?: Record<string, unknown>;
      };
      // Check if pricing is indeterminate:
      // - Primary is unverified or active with no benefits
      // - Primary is active but secondary is unverified (can't determine final price)
      const primaryStatus = err.insuranceStatuses?.[0]?.status;
      const secondaryStatus = err.insuranceStatuses?.[1]?.status;
      const isUnverified =
        primaryStatus === InsurancesStatusEnum.Unverified ||
        primaryStatus === InsurancesStatusEnum.ActiveBenefitsNotReturned ||
        (primaryStatus === InsurancesStatusEnum.Active &&
          secondaryStatus === InsurancesStatusEnum.Unverified);

      const output = {
        insuranceError: {
          code: err.code,
          message: err.message,
          retryable: err.retryable,
          insuranceError: err.insuranceError,
          insuranceStates: err.insuranceStates,
          insuranceStatuses: err.insuranceStatuses,
        },
        insurances: err.insurances, // Include formatted insurance data for context
        pendingPricesMode: isUnverified ? true : undefined,
      };
      return output;
    },
  },
};
